import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  FaClock,
  FaInfinity,
  FaCrosshairs,
  FaTrophy,
  FaQuestionCircle,
  FaRocket,
  FaInfoCircle
} from 'react-icons/fa';
import Button from '../components/common/Button';
import GameBackground from '../components/game/GameBackground';
import HelpPanel from '../components/game/HelpPanel';
import AchievementsPanel from '../components/game/AchievementsPanel';
import UpgradeInfoModal from '../components/game/UpgradeInfoModal';
import Footer from '../components/common/Footer';
import FAQSection from '../components/common/FAQSection';
import SEOHead from '../components/SEO/SEOHead';

const Home = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showUpgradeInfo, setShowUpgradeInfo] = useState(false);

  const modes = [
    {
      id: 'timeChallenge',
      icon: FaClock,
      title: '時間挑戰怎麼玩？',
      accessibleTitle: '時間挑戰',
      desc: '60秒內收集角色獲得最高分數',
      color: 'from-pink-500 to-pink-600'
    },
    {
      id: 'endless',
      icon: FaInfinity,
      title: '無盡模式有時間限制嗎？',
      accessibleTitle: '無盡模式',
      desc: '無時間限制，角色逐漸加速',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'precision',
      icon: FaCrosshairs,
      title: '精準模式如何計分？',
      accessibleTitle: '精準模式',
      desc: '錯過角色會扣分，限時60秒',
      color: 'from-blue-500 to-blue-600'
    }
  ];

  return (
    <>
      <SEOHead
        title="泡泡樂園 - 最好玩的免費泡泡點擊遊戲 | 挑戰連擊解鎖成就"
        description="泡泡樂園是一款完全免費、無需下載安裝的線上點擊小遊戲，提供時間挑戰、無盡模式與精準模式三種玩法，點擊可愛泡泡角色快速累積連擊分數，收集金色、時間、爆炸與連擊等特殊道具泡泡，挑戰解鎖25個以上成就，支援手機、平板與電腦瀏覽器直接遊玩，適合各年齡層休閒娛樂。"
        canonical="https://bubble.homy.hk/"
      />
      <GameBackground />
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <header className="text-center mb-8 animate-float">
            <h1 className="text-5xl sm:text-7xl font-bold mb-4 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent drop-shadow-lg">
              🎈 泡泡樂園
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 font-medium">
              點擊可愛的泡泡角色獲得分數！收集不同角色解鎖成就！
            </p>
          </header>

          <section
            id="modes"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full mb-8"
            aria-label="遊戲模式選擇"
          >
            {modes.map((mode) => {
              const Icon = mode.icon;

              return (
                <article
                  key={mode.id}
                  className="group bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
                >
                  <button
                    onClick={() => navigate(`/play?mode=${mode.id}`)}
                    className="w-full h-full text-left hover:scale-105 active:scale-95 transition-transform"
                    aria-label={`選擇${mode.accessibleTitle}模式`}
                  >
                    <div
                      className={`w-20 h-20 bg-gradient-to-br ${mode.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}
                    >
                      <Icon className="text-white text-4xl" aria-hidden="true" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                      {mode.title}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {mode.desc}
                    </p>
                  </button>
                </article>
              );
            })}
          </section>

          <nav
            className="flex gap-4 flex-wrap justify-center mb-10"
            aria-label="遊戲功能"
          >
            <Button
              onClick={() => setShowUpgradeInfo(true)}
              variant="primary"
              className="shadow-lg"
              aria-label="查看升級系統說明"
            >
              <FaRocket aria-hidden="true" />
              升級系統
            </Button>
            <Button
              onClick={() => setShowAchievements(true)}
              variant="accent"
              className="shadow-lg"
              aria-label="查看成就列表"
            >
              <FaTrophy aria-hidden="true" />
              成就
            </Button>
            <Button
              onClick={() => setShowHelp(true)}
              variant="secondary"
              className="shadow-lg"
              aria-label="查看遊戲幫助"
            >
              <FaQuestionCircle aria-hidden="true" />
              如何遊玩
            </Button>
            <Button
              onClick={() => navigate('/about')}
              variant="secondary"
              className="shadow-lg"
              aria-label="關於我們"
            >
              <FaInfoCircle aria-hidden="true" />
              關於我們
            </Button>
          </nav>

          <FAQSection />
        </main>
        <Footer />
      </div>

      <UpgradeInfoModal
        isOpen={showUpgradeInfo}
        onClose={() => setShowUpgradeInfo(false)}
      />
      <HelpPanel
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      <AchievementsPanel
        isOpen={showAchievements}
        onClose={() => setShowAchievements(false)}
        unlockedIds={{}}
      />
    </>
  );
};

export default Home;